import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Input,
  Label,
  FormGroup,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Spinner,
  Table
} from 'reactstrap';
import { getTimeSlots, createTimeSlot, updateTimeSlot, deleteTimeSlot } from '../../../api/api';
import { useSede } from '../../../context/SedeContext';
import toast from 'react-hot-toast';

const TimeSlotManager = () => {
  const { getSedeId, getSedeIds, selectedSedes, availableSedes } = useSede();
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [formData, setFormData] = useState({
    start_time: '',
    end_time: '',
    is_active: true
  });

  const sedeId = useMemo(() => {
    const single = getSedeId();
    if (single) return single;
    const ids = typeof getSedeIds === 'function' ? getSedeIds() : [];
    if (ids?.length) return ids[0];
    if (selectedSedes?.length) return selectedSedes[0].id;
    if (availableSedes?.length) return availableSedes[0].id;
    const envDefault = process.env.REACT_APP_DEFAULT_SEDE;
    return envDefault ? Number(envDefault) || envDefault : null;
  }, [getSedeId, getSedeIds, selectedSedes, availableSedes]);

  const sedeLabel = useMemo(() => {
    const match =
      selectedSedes.find((s) => s.id === sedeId) ||
      availableSedes.find((s) => s.id === sedeId);
    return match?.name || 'la sede';
  }, [sedeId, selectedSedes, availableSedes]);

  useEffect(() => {
    if (sedeId) {
      loadTimeSlots();
    } else {
      setTimeSlots([]);
    }
  }, [sedeId]);

  const loadTimeSlots = async () => {
    setLoading(true);
    try {
      const slots = await getTimeSlots(sedeId);
      setTimeSlots(slots);
    } catch (error) {
      console.error('Error loading time slots:', error);
      toast.error('Error al cargar horarios');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.start_time || !formData.end_time) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (formData.start_time >= formData.end_time) {
      toast.error('La hora de inicio debe ser anterior a la hora de fin');
      return;
    }

    try {
      const timeSlotData = {
        sede_id: sedeId,
        start_time: formData.start_time,
        end_time: formData.end_time,
        is_active: formData.is_active
      };

      if (editingSlot) {
        await updateTimeSlot(editingSlot.id, timeSlotData);
        toast.success('Horario actualizado correctamente');
      } else {
        await createTimeSlot(timeSlotData);
        toast.success('Horario creado correctamente');
      }
      
      setModalOpen(false);
      setEditingSlot(null);
      setFormData({ start_time: '', end_time: '', is_active: true });
      loadTimeSlots();
    } catch (error) {
      console.error('Error saving time slot:', error);
      toast.error('Error al guardar horario');
    }
  };

  const handleEdit = (slot) => {
    setEditingSlot(slot);
    setFormData({
      start_time: slot.start_time,
      end_time: slot.end_time,
      is_active: slot.is_active
    });
    setModalOpen(true);
  };

  const handleDelete = async (slotId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este horario?')) {
      try {
        await deleteTimeSlot(slotId);
        toast.success('Horario eliminado correctamente');
        loadTimeSlots();
      } catch (error) {
        console.error('Error deleting time slot:', error);
        toast.error('Error al eliminar horario');
      }
    }
  };

  const resetForm = () => {
    setFormData({ start_time: '', end_time: '', is_active: true });
    setEditingSlot(null);
  };

  if (!sedeId) {
    return (
      <Card>
        <CardBody className="text-center">
          <Alert color="warning">
            <i className="fa fa-exclamation-triangle mr-2"></i>
            No hay sede disponible para gestionar horarios
          </Alert>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Gestión de Horarios</h2>
          <p className="text-muted mb-0">
            Administra los horarios disponibles para {sedeLabel}
          </p>
        </div>
        <Button 
          color="primary" 
          onClick={() => {
            resetForm();
            setModalOpen(true);
          }}
        >
          <i className="fa fa-plus mr-2"></i>
          Nuevo Horario
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <i className="fa fa-clock mr-2"></i>
            Horarios Configurados
            <Badge color="secondary" className="ml-2">
              {timeSlots.length} horarios
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Cargando horarios...</p>
            </div>
          ) : timeSlots.length === 0 ? (
            <div className="text-center py-4">
              <i className="fa fa-clock text-muted mb-3" style={{ fontSize: '3em' }}></i>
              <h5>No hay horarios configurados</h5>
              <p className="text-muted">
                Crea el primer horario para esta sede
              </p>
            </div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Hora de Inicio</th>
                  <th>Hora de Fin</th>
                  <th>Duración</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {timeSlots.map(slot => (
                  <tr key={slot.id}>
                    <td>
                      <strong>{slot.start_time}</strong>
                    </td>
                    <td>
                      <strong>{slot.end_time}</strong>
                    </td>
                    <td>
                      <Badge color="info">
                        {slot.time_slot_display}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={slot.is_active ? "success" : "secondary"}>
                        {slot.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        color="warning"
                        className="mr-2"
                        onClick={() => handleEdit(slot)}
                      >
                        <i className="fa fa-edit"></i>
                      </Button>
                      <Button
                        size="sm"
                        color="danger"
                        onClick={() => handleDelete(slot.id)}
                      >
                        <i className="fa fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="md">
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editingSlot ? 'Editar Horario' : 'Nuevo Horario'}
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="start_time">Hora de Inicio</Label>
                  <Input
                    type="time"
                    id="start_time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="end_time">Hora de Fin</Label>
                  <Input
                    type="time"
                    id="end_time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <Label className="custom-control-label" for="is_active">
                  Horario activo
                </Label>
              </div>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button color="primary" type="submit">
              {editingSlot ? 'Actualizar' : 'Crear'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default TimeSlotManager;
